// skipcq: JS-W1028
import { useCallback, useMemo, useState } from 'react';
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import PropTypes from 'prop-types';
import { buildStateTableRows } from '../../utils/beerActivityByStateReport';

const SORT_COLUMNS = {
  stateName: 'stateName',
  beerCountInDatabase: 'beerCountInDatabase',
  beerReviewCount: 'beerReviewCount',
  beerCheckinCount: 'beerCheckinCount',
  activeUserDays: 'activeUserDays',
  avgActivitiesPerActiveUserDay: 'avgActivitiesPerActiveUserDay',
};

const SORTABLE_COLUMNS = [
  { key: SORT_COLUMNS.stateName, label: 'State', align: 'left' },
  { key: SORT_COLUMNS.beerCountInDatabase, label: 'Beers in DB', align: 'right' },
  { key: SORT_COLUMNS.beerReviewCount, label: 'Reviews', align: 'right' },
  { key: SORT_COLUMNS.beerCheckinCount, label: 'Log entries', align: 'right' },
  { key: SORT_COLUMNS.activeUserDays, label: 'Active user-days', align: 'right' },
  {
    key: SORT_COLUMNS.avgActivitiesPerActiveUserDay,
    label: 'Avg / user-day',
    align: 'right',
  },
];

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {string} sortKey
 * @param {string} sortDirection
 * @returns {Array<Record<string, unknown>>}
 */
function sortTableRows(rows, sortKey, sortDirection) {
  const sortedRows = [...rows];
  sortedRows.sort((leftRow, rightRow) => {
    const leftValue = leftRow[sortKey];
    const rightValue = rightRow[sortKey];
    if (typeof leftValue === 'string' && typeof rightValue === 'string') {
      const comparison = leftValue.localeCompare(rightValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    }
    const leftNumber = typeof leftValue === 'number' ? leftValue : 0;
    const rightNumber = typeof rightValue === 'number' ? rightValue : 0;
    const comparison = leftNumber - rightNumber;
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  return sortedRows;
}

/**
 * @param {Object} props
 * @param {string} props.sortKey
 * @param {string} props.sortDirection
 * @param {Function} props.onSortLabelClick
 */
function BeerActivityByStateTableHead({ sortKey, sortDirection, onSortLabelClick }) {
  return (
    <TableHead>
      <TableRow>
        {SORTABLE_COLUMNS.map((column) => (
          <TableCell
            key={column.key}
            align={column.align}
            sortDirection={
              sortKey === column.key ? sortDirection : false
            }
          >
            <TableSortLabel
              active={sortKey === column.key}
              direction={sortKey === column.key ? sortDirection : 'asc'}
              onClick={onSortLabelClick}
              data-sort-key={column.key}
            >
              {column.label}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell>Beer in DB</TableCell>
      </TableRow>
    </TableHead>
  );
}

BeerActivityByStateTableHead.propTypes = {
  sortKey: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  onSortLabelClick: PropTypes.func.isRequired,
};

function BeerActivityByStateTable({ byState }) {
  const [sortKey, setSortKey] = useState(SORT_COLUMNS.avgActivitiesPerActiveUserDay);
  const [sortDirection, setSortDirection] = useState('desc');

  const tableRows = useMemo(() => buildStateTableRows(byState), [byState]);
  const sortedRows = useMemo(
    () => sortTableRows(tableRows, sortKey, sortDirection),
    [tableRows, sortKey, sortDirection],
  );

  const handleSort = useCallback((columnKey) => {
    if (sortKey === columnKey) {
      setSortDirection((currentDirection) =>
        currentDirection === 'asc' ? 'desc' : 'asc',
      );
      return;
    }
    setSortKey(columnKey);
    setSortDirection(columnKey === SORT_COLUMNS.stateName ? 'asc' : 'desc');
  }, [sortKey]);

  const handleSortLabelClick = useCallback((event) => {
    const columnKey = event.currentTarget.getAttribute('data-sort-key');
    if (columnKey) {
      handleSort(columnKey);
    }
  }, [handleSort]);

  return (
    <TableContainer>
      <Table size="small" aria-label="Beer activity by state">
        <BeerActivityByStateTableHead
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSortLabelClick={handleSortLabelClick}
        />
        <TableBody>
          {sortedRows.map((row) => (
            <TableRow key={row.stateCode} hover>
              <TableCell>{row.stateName}</TableCell>
              <TableCell align="right">{row.beerCountInDatabase}</TableCell>
              <TableCell align="right">{row.beerReviewCount}</TableCell>
              <TableCell align="right">{row.beerCheckinCount}</TableCell>
              <TableCell align="right">{row.activeUserDays}</TableCell>
              <TableCell align="right">{row.avgActivitiesPerActiveUserDay.toFixed(2)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.hasBeerInDatabase ? 'Yes' : 'No'}
                  color={row.hasBeerInDatabase ? 'success' : 'default'}
                  variant="outlined"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

BeerActivityByStateTable.propTypes = {
  byState: PropTypes.object.isRequired,
};

export default BeerActivityByStateTable;
